import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  IconButton,
  InputAdornment,
  InputLabel,
  Stack,
  TextField,
  TextFieldProps,
} from '@mui/material'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useEditMode } from '../../../contexts/edit-mode.context'
import { useBlockBrowserAutofill } from '../../../hooks/use-block-browser-autofill'
import {
  EDITABLE_VALUE_COLOR,
  READ_ONLY_VALUE_COLOR,
  readOnlyTextFieldSx,
} from '../field-read-only.styles'

type TextInputFieldProps = {
  highlight?: boolean
  bold?: boolean
  currency?: boolean
  removeBorder?: boolean
  prefilledLight?: boolean
  showPasswordToggle?: boolean
  /** Opt-in label/input row. Default stays vertical for all existing screens. */
  layout?: 'vertical' | 'horizontal'
  transform?: (value: string) => string
  maxLength?: number
} & TextFieldProps

const TextInputField = ({
  label,
  placeholder = 'Answer',
  highlight,
  bold,
  name,
  currency,
  removeBorder,
  prefilledLight,
  showPasswordToggle,
  layout = 'vertical',
  type,
  autoComplete,
  transform,
  maxLength,
  required,
  ...rest
}: TextInputFieldProps) => {
  const { control } = useFormContext()
  const { isEditMode, hasContext } = useEditMode()
  const { blockBrowserAutofill, domFieldName, domFieldId, htmlInputProps } =
    useBlockBrowserAutofill(name)
  const [isFocused, setIsFocused] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const inputType =
    showPasswordToggle && type === 'password'
      ? showPassword
        ? 'text'
        : 'password'
      : type
  const isDisabled =
    rest.disabled !== undefined ? rest.disabled : hasContext ? !isEditMode : false
  // Soft read-only chrome only when gated by EditModeProvider (order details view)
  const isViewMode = hasContext && !isEditMode && isDisabled
  const valueColor = isViewMode ? READ_ONLY_VALUE_COLOR : EDITABLE_VALUE_COLOR
  const isHorizontal = layout === 'horizontal'

  return (
    <Stack
      spacing={isHorizontal ? 0 : 1}
      sx={
        isHorizontal
          ? {
              display: 'grid',
              gridTemplateColumns: '168px minmax(0, 1fr)',
              columnGap: 2.5,
              alignItems: 'center',
              width: '100%',
            }
          : undefined
      }
    >
      <InputLabel
        sx={{
          color: required
            ? '#000'
            : isHorizontal || isViewMode
              ? 'text.secondary'
              : highlight
                ? 'error.main'
                : 'secondary.dark',
          fontWeight: required
            ? 500
            : isHorizontal
              ? 500
              : isViewMode
                ? 500
                : highlight || bold
                  ? 700
                  : 300,
          ...(isHorizontal && {
            fontSize: '0.875rem',
            lineHeight: 1.35,
            whiteSpace: 'normal',
          }),
          ...(isViewMode &&
            !isHorizontal && {
              fontSize: '0.875rem',
              fontWeight: 500,
            }),
        }}
      >
        {label}
      </InputLabel>
      <Controller
        name={name ?? ''}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            placeholder={
              hasContext && (!isEditMode || !isHorizontal) ? '' : placeholder
            }
            size="small"
            name={domFieldName}
            id={domFieldId}
            helperText={error?.message}
            error={!!error}
            variant={removeBorder ? 'standard' : 'outlined'}
            InputProps={{ disableUnderline: removeBorder }}
            sx={
              isViewMode
                ? readOnlyTextFieldSx
                : {
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: valueColor,
                      color: valueColor,
                    },
                  }
            }
            type={inputType}
            slotProps={{
              input: {
                startAdornment: !!currency && (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: showPasswordToggle && type === 'password' && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  color:
                    prefilledLight && field.value && !isFocused
                      ? 'text.secondary'
                      : field.value
                        ? valueColor
                        : undefined,
                },
              },
              htmlInput: {
                ...htmlInputProps,
                ...(maxLength ? { maxLength } : {}),
                autoComplete: blockBrowserAutofill
                  ? 'new-password'
                  : autoComplete ?? (hasContext ? 'off' : undefined),
              },
            }}
            disabled={isDisabled}
            required={required}
            {...rest}
            onChange={(e) => {
              const next = transform ? transform(e.target.value) : e.target.value
              field.onChange(next)
              rest.onChange?.(e)
            }}
            onFocus={(e) => {
              setIsFocused(true)
              rest.onFocus?.(e)
            }}
            onBlur={(e) => {
              setIsFocused(false)
              rest.onBlur?.(e)
            }}
          />
        )}
      />
    </Stack>
  )
}

export default TextInputField
